import type {
  LearnerFormFieldStorage,
  LearnerFormResponse,
  LearnerFormStandardField,
  LearnerFormStandardFieldKind,
  LearnerFormVertical,
  TenantVerticalLiteral,
} from "@eduviaos/shared";

/**
 * Phase SC-22 — client copy of the backend's standard learner-field
 * catalogue (`app/domains/tenant/learner_form_catalog.py`).
 *
 * Used only when `GET /me/learner-form` fails, so the add/edit learner
 * sheet still opens with the vertical's default fields. The backend
 * remains the authority: it re-applies the tenant's form on every save.
 * Keep the order, labels and per-vertical visibility in step with the
 * Python catalogue.
 */
interface CatalogEntry {
  key: string;
  kind: LearnerFormStandardFieldKind;
  storage: LearnerFormFieldStorage;
  labels: { default: string } & Partial<Record<LearnerFormVertical, string>>;
  hiddenFor?: readonly LearnerFormVertical[];
  locked?: boolean;
}

const NO_GRADES: readonly LearnerFormVertical[] = ["study_centre", "coaching_institute"];
const SC_ONLY: readonly LearnerFormVertical[] = ["study_centre"];

const CATALOG: readonly CatalogEntry[] = [
  { key: "name", kind: "text", storage: "column", labels: { default: "Full name" }, locked: true },
  { key: "email", kind: "email", storage: "column", labels: { default: "Email" } },
  { key: "phone", kind: "phone", storage: "column", labels: { default: "Phone" } },
  { key: "date_of_birth", kind: "date", storage: "column", labels: { default: "Date of birth" } },
  { key: "gender", kind: "select", storage: "column", labels: { default: "Gender" } },
  {
    key: "roll_number",
    kind: "text",
    storage: "column",
    labels: { default: "Roll number", study_centre: "Learner ID" },
  },
  {
    key: "admission_number",
    kind: "text",
    storage: "column",
    labels: { default: "Admission number", study_centre: "Membership no." },
  },
  {
    key: "admission_date",
    kind: "date",
    storage: "column",
    labels: { default: "Admission date", study_centre: "Joining date" },
  },
  {
    key: "parent_name",
    kind: "text",
    storage: "metadata",
    labels: { default: "Parent / guardian name", study_centre: "Guardian name" },
    hiddenFor: SC_ONLY,
  },
  {
    key: "parent_email",
    kind: "email",
    storage: "metadata",
    labels: { default: "Parent email", study_centre: "Guardian email" },
    hiddenFor: SC_ONLY,
  },
  {
    key: "parent_phone",
    kind: "phone",
    storage: "metadata",
    labels: { default: "Parent phone", study_centre: "Guardian phone" },
    hiddenFor: SC_ONLY,
  },
  {
    key: "grade",
    kind: "text",
    storage: "column",
    labels: { default: "Grade" },
    hiddenFor: NO_GRADES,
  },
  {
    key: "section",
    kind: "text",
    storage: "column",
    labels: { default: "Section" },
    hiddenFor: NO_GRADES,
  },
  {
    key: "stage",
    kind: "select",
    storage: "column",
    labels: { default: "NEP stage" },
    hiddenFor: NO_GRADES,
  },
  { key: "blood_group", kind: "text", storage: "column", labels: { default: "Blood group" } },
  {
    key: "primary_language",
    kind: "text",
    storage: "column",
    labels: { default: "Primary language" },
  },
  {
    key: "aadhaar_reference",
    kind: "textarea",
    storage: "column",
    labels: { default: "Aadhaar reference" },
  },
  { key: "address", kind: "textarea", storage: "metadata", labels: { default: "Address" } },
  { key: "notes", kind: "textarea", storage: "metadata", labels: { default: "Notes" } },
];

/** The vertical's default learner form, shaped like `GET /me/learner-form`. */
export function defaultLearnerForm(vertical: TenantVerticalLiteral): LearnerFormResponse {
  const v: LearnerFormVertical = vertical;
  const fields: LearnerFormStandardField[] = CATALOG.map((entry, index) => {
    const label = entry.labels[v] ?? entry.labels.default;
    return {
      key: entry.key,
      kind: entry.kind,
      storage: entry.storage,
      label,
      default_label: label,
      visible: entry.locked === true || !(entry.hiddenFor ?? []).includes(v),
      required: entry.locked === true,
      order: index * 10,
      help_text: null,
      locked: entry.locked === true,
    };
  });
  return {
    vertical: v,
    fields,
    custom_fields: [],
    editable_by_tenant: false,
    is_customised: false,
    max_custom_fields: 40,
  };
}
